import { motion } from 'framer-motion';
import { Logo } from './common/Logo';

export type LoaderSize = 'sm' | 'md' | 'lg' | 'xl';
export type LoaderVariant = 'page' | 'inline' | 'overlay';

interface LogoLoaderProps {
    size?: LoaderSize;
    variant?: LoaderVariant;
    message?: string;
    showLogo?: boolean;
}

const SIZE_CONFIG: Record<LoaderSize, { logo: string; ring: string; container: string; containerSize: string }> = {
    sm: { logo: 'w-6 h-6', ring: 'w-10 h-10', container: 'p-2', containerSize: 'w-10 h-10' },
    md: { logo: 'w-8 h-8', ring: 'w-14 h-14', container: 'p-3', containerSize: 'w-14 h-14' },
    lg: { logo: 'w-12 h-12', ring: 'w-20 h-20', container: 'p-4', containerSize: 'w-20 h-20' },
    xl: { logo: 'w-16 h-16', ring: 'w-28 h-28', container: 'p-6', containerSize: 'w-28 h-28' },
};

/**
 * LogoLoader - A unified loading component using the Dashbal logo
 * 
 * Variants:
 * - 'page': Full page loader with centered logo (for page loads)
 * - 'inline': Inline loader for components (for sections)
 * - 'overlay': Semi-transparent overlay loader
 */
export function LogoLoader({
    size = 'lg',
    variant = 'inline',
    message,
    showLogo = true,
}: LogoLoaderProps) {
    const sizeConfig = SIZE_CONFIG[size];

    const loaderContent = (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="flex flex-col items-center justify-center gap-4"
        >
            {showLogo && (
                <div className={`relative ${sizeConfig.containerSize} flex items-center justify-center`}>
                    {/* Pulsing background glow */}
                    <motion.div
                        animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.5, 0.3] }}
                        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                        className={`absolute inset-0 bg-accent-primary/20 rounded-full blur-xl`}
                    />

                    {/* Spinning ring */}
                    <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                        className={`absolute inset-0 rounded-full border-2 border-transparent border-t-accent-primary border-r-accent-primary/30`}
                    />

                    {/* Logo with subtle pulse */}
                    <motion.div
                        animate={{ scale: [1, 1.05, 1] }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                        className="relative z-10 flex items-center justify-center"
                    >
                        <Logo className={`${sizeConfig.logo} text-accent-primary block`} />
                    </motion.div>
                </div>
            )}

            {message && (
                <motion.p
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-sm text-neutral-500 font-medium"
                >
                    {message}
                </motion.p>
            )}
        </motion.div>
    );

    // Page loader - full screen centered
    if (variant === 'page') {
        return (
            <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950 flex items-center justify-center">
                {loaderContent}
            </div>
        );
    }

    // Overlay loader - semi-transparent background
    if (variant === 'overlay') {
        return (
            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50">
                {loaderContent}
            </div>
        );
    }

    // Inline loader - just the spinner
    return (
        <div className="flex items-center justify-center py-12">
            {loaderContent}
        </div>
    );
}

/**
 * LoadingBar - A simple top loading bar for navigation transitions
 */
export function LoadingBar() {
    return (
        <div className="fixed top-0 left-0 right-0 z-50 h-0.5 bg-transparent overflow-hidden">
            <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: '100%' }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                className="h-full w-1/3 bg-gradient-to-r from-transparent via-accent-primary to-transparent"
            />
        </div>
    );
}

/**
 * PageLoader - Convenience component for full-page loading states
 */
export function PageLoader({ message }: { message?: string }) {
    return <LogoLoader variant="page" size="lg" message={message} />;
}

/**
 * SectionLoader - Convenience component for section/component loading states
 */
export function SectionLoader({ message, size = 'md' }: { message?: string; size?: LoaderSize }) {
    return <LogoLoader variant="inline" size={size} message={message} />;
}

/**
 * OverlayLoader - Convenience component for overlay loading states
 */
export function OverlayLoader({ message }: { message?: string }) {
    return <LogoLoader variant="overlay" size="md" message={message} />;
}

/**
 * Spinner - A simple spinning loader for buttons and small inline uses
 * Uses the accent color and matches the visual style of the logo loader
 */
export function Spinner({
    size = 'sm',
    className = ''
}: {
    size?: 'xs' | 'sm' | 'md' | 'lg';
    className?: string;
}) {
    const sizeClasses = {
        xs: 'w-3 h-3 border',
        sm: 'w-4 h-4 border-2',
        md: 'w-6 h-6 border-2',
        lg: 'w-8 h-8 border-2',
    };

    return (
        <div
            className={`flex-shrink-0 ${sizeClasses[size]} border-current border-t-transparent rounded-full animate-spin ${className}`}
            style={{ transformOrigin: 'center' }}
            role="status"
            aria-label="Loading"
        />
    );
}

/**
 * ButtonSpinner - Spinner specifically styled for use inside buttons
 */
export function ButtonSpinner({ className = '' }: { className?: string }) {
    return <Spinner size="sm" className={`text-current ${className}`} />;
}

export default LogoLoader;
